import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { IMG_CAREVAN, IMG_CLINIC } from "@/lib/images";
import { CalendarHeart, Sparkles, Users, ShieldCheck, ArrowRight } from "lucide-react";

const HERO = IMG_CAREVAN;
const DENTIST = IMG_CLINIC;

export default function Home() {
  return (
    <div className="App">
      <Navbar />

      {/* Hero - desktop */}
      <section className="hidden lg:flex relative min-h-[92vh] items-center overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img src={HERO} alt="Utopia Dental Care mobile clinic" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2D2336]/85 via-[#2D2336]/55 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-2xl">
            <p className="animate-fade-in-up label text-sm uppercase tracking-[0.2em] text-[#C8B3D6] font-semibold">
              Albuquerque, New Mexico
            </p>
            <h1 className="animate-fade-in-up delay-100 font-serif text-5xl xl:text-7xl text-white mt-4 leading-[1.05]">
              A convenient, trusted dental experience
            </h1>
            <p className="animate-fade-in-up delay-200 text-lg text-[#E5DEEB] mt-6 leading-relaxed max-w-xl">
              The highest standard of quality dental health — delivered with skill,
              warmth, and care you can count on for every smile in our community.
            </p>
            <div className="animate-fade-in-up delay-300 flex flex-wrap gap-4 mt-10">
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="bg-[#6B4C8A] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#543C6E] transition-all hover:-translate-y-0.5 shadow-lg flex items-center gap-2"
              >
                Book an Appointment <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/request-service"
                data-testid="hero-request-btn"
                className="bg-white/10 backdrop-blur text-white border border-white/30 px-8 py-3.5 rounded-full font-medium hover:bg-white/20 transition-all"
              >
                Request Community Service
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Hero - mobile */}
      <section className="lg:hidden" data-testid="hero-mobile">
        <div className="pt-28 px-6">
          <p className="label text-sm uppercase tracking-[0.2em] text-[#6B4C8A] font-semibold">
            Albuquerque, New Mexico
          </p>
        </div>
        <img src={HERO} alt="Utopia Dental Care mobile clinic" className="w-full h-60 object-cover mt-4" />
        <div className="px-6 py-8">
          <h1 className="font-serif text-3xl text-[#2D2336] leading-tight">
            A convenient, trusted dental experience
          </h1>
          <p className="text-base text-[#5C5166] mt-4 leading-relaxed">
            The highest standard of quality dental health — delivered with skill,
            warmth, and care you can count on for every smile in our community.
          </p>
          <div className="flex flex-col gap-3 mt-6">
            <Link to="/book" className="bg-[#6B4C8A] text-white px-6 py-3.5 rounded-full font-medium text-center flex items-center justify-center gap-2">
              Book an Appointment <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/request-service" className="border border-[#6B4C8A] text-[#6B4C8A] px-6 py-3.5 rounded-full font-medium text-center">
              Request Community Service
            </Link>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <p className="label text-sm uppercase tracking-[0.15em] text-[#78698A] font-semibold text-center">
          Who We Are
        </p>
        <div className="grid md:grid-cols-2 gap-8 mt-10">
          <div className="bg-white border border-[#E5DEEB] rounded-2xl p-10 shadow-sm hover:shadow-md transition-shadow">
            <div className="h-12 w-12 rounded-full bg-[#E5DEEB] flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-[#6B4C8A]" />
            </div>
            <h2 className="font-serif text-3xl text-[#2D2336] mt-6">Our Mission</h2>
            <p className="text-lg text-[#5C5166] leading-relaxed mt-4 italic">
              “To offer our patients the highest standard of quality dental health
              care available with skill and compassion.”
            </p>
          </div>
          <div className="bg-[#6B4C8A] rounded-2xl p-10 shadow-md text-white">
            <div className="h-12 w-12 rounded-full bg-white/15 flex items-center justify-center">
              <CalendarHeart className="h-6 w-6 text-white" />
            </div>
            <h2 className="font-serif text-3xl mt-6">Our Vision</h2>
            <p className="text-lg leading-relaxed mt-4 italic text-[#EDE7F2]">
              “To expand our services with mobile units to treat the underserved
              pediatric and elderly population of New Mexico.”
            </p>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="bg-white border-y border-[#E5DEEB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid md:grid-cols-3 gap-12">
          {[
            { icon: ShieldCheck, t: "Insurance Welcome", d: "We accept a wide range of dental insurance plans — enter yours when you book." },
            { icon: Users, t: "No Insurance? No Problem", d: "Utopia Mobile Care, our 501(c)(3), will reach out to help you get the care you need." },
            { icon: CalendarHeart, t: "By Appointment", d: "Convenient scheduling so every visit is easy, unhurried, and centered on you." },
          ].map((f, i) => (
            <div key={i} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <f.icon className="h-8 w-8 text-[#6B4C8A]" />
              <h3 className="font-serif text-2xl text-[#2D2336] mt-4">{f.t}</h3>
              <p className="text-[#5C5166] leading-relaxed mt-2">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA with image */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid md:grid-cols-2 gap-14 items-center">
        <div className="hidden md:block rounded-2xl overflow-hidden shadow-lg">
          <img src={DENTIST} alt="Utopia Dental Care team" className="w-full h-[480px] object-cover" />
        </div>
        <div>
          <h2 className="font-serif text-4xl text-[#2D2336]">Care that meets you where you are</h2>
          <p className="text-lg text-[#5C5166] leading-relaxed mt-5">
            Whether you're visiting our Albuquerque practice or being served through
            our mobile units, our team treats every patient with dignity and skill.
          </p>
          <Link
            to="/book"
            data-testid="cta-book-btn"
            className="inline-flex items-center gap-2 mt-8 bg-[#6B4C8A] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#543C6E] transition-all hover:-translate-y-0.5 shadow-sm"
          >
            Schedule Your Visit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
